import { ColumnConfig, FieldDefinition } from '../field-definition';
import { PreAccessFilterOptions } from '../../pre-access-filter/models/pre-access-filter.models';
import { FieldDependencyRule } from '../../generic-form/model/field-dependency';
import { getFieldBuilderNamespace } from './field-builder.namespace';

export abstract class BaseFieldBuilder {
  protected sortableValue = true;
  protected filterableValue = true;
  protected formatterValue?: any;
  protected placeholderValue?: string;
  protected widthValue?: number;
  protected minWidthValue?: number;
  protected hideInChildFormsValue = false;
  protected hideByDefaultValue = false;
  protected readOnlyValue = false;
  protected showReadOnlyInFormValue = false;
  protected dependencyRules: FieldDependencyRule[] = [];
  protected _type?: string;
  protected _controlType: string;
  protected prefilterOptions?: PreAccessFilterOptions;
  protected filterInputEmptyValueValue?: string;

  constructor(protected key: string, protected label: string, controlType: string) {
    this._controlType = controlType;
  }

  hideByDefault(): this {
    this.hideByDefaultValue = true;
    return this;
  }

  hideInChildForms(): this {
    this.hideInChildFormsValue = true;
    return this;
  }

  readOnly(): this {
    this.readOnlyValue = true;
    return this;
  }

  showReadOnlyInForm(): this {
    this.showReadOnlyInFormValue = true;
    return this;
  }

  sortable(value: boolean): this {
    this.sortableValue = value;
    return this;
  }

  filterable(value: boolean): this {
    this.filterableValue = value;
    return this;
  }

  formatter(formatter: any): this {
    this.formatterValue = formatter;
    return this;
  }

  placeholder(value: string): this {
    this.placeholderValue = value;
    return this;
  }

  gridColumnWidth(width: number): this {
    this.widthValue = width;
    return this;
  }

  gridColumnMinWidth(minWidth: number): this {
    this.minWidthValue = minWidth;
    return this;
  }

  /**
   * Configure this field to appear in the pre-access filter modal.
   * @param options Configuration options for the pre-access filter
   */
  prefilter(options: PreAccessFilterOptions = {}): this {
    this.prefilterOptions = options;
    return this;
  }

  filterInputEmptyValue(value: string): this {
    this.filterInputEmptyValueValue = value;
    return this;
  }

  dependsOn(rules: FieldDependencyRule | FieldDependencyRule[]): this {
    const normalized = Array.isArray(rules) ? rules : [rules];
    this.dependencyRules.push(...normalized);
    return this;
  }

  protected getLocalizationKey(): string {
    const ns = getFieldBuilderNamespace();
    return ns ? `${ns}::${this.label}` : this.label;
  }

  protected getBaseColumnConfig(): ColumnConfig {
    const config: ColumnConfig = {
      id: this.key,
      field: this.key,
      name: this.getLocalizationKey(),
      sortable: this.sortableValue,
      filterable: this.filterableValue,
    };

    if (this.prefilterOptions?.queryKey) {
      config.params = {
        ...config.params,
        queryFieldFilter: this.prefilterOptions.queryKey,
      };
    }

    if (this.formatterValue) {
      config.formatter = this.formatterValue;
    }

    if (this.widthValue) {
      config.width = this.widthValue;
    }

    if (this.minWidthValue) {
      config.minWidth = this.minWidthValue;
    }

    if (this._type) {
      config.type = this._type as any;
    }

    if (this.readOnlyValue) {
      (config as any).editable = false;
      (config as any).focusable = false;
    }

    return config;
  }

  abstract build(): FieldDefinition;
}
